// src/pages/Catalog/components/ProductsTab.js
import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../../services/socket-io";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, Box } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import api from "../../../services/api";
import MainHeaderButtonsWrapper from "../../../components/MainHeaderButtonsWrapper";
import ProductCard from "../ProductCard";
import ProductModal from "../ProductModal";
import { i18n } from "../../../translate/i18n";
import toastError from "../../../errors/toastError";
import ProductCardSkeleton from "../ProductCardSkeleton";
import { AuthContext } from "../../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  header: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  searchAndAddContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  searchInput: {
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  addButton: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  filtersContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
    },
  },
  filterItem: {
    minWidth: 200,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  productsGrid: {
    width: '100%',
    margin: 0,
    padding: theme.spacing(2),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PRODUCTS") {
    const products = action.payload;
    const newProducts = [];

    products.forEach((product) => {
      const productIndex = state.findIndex((p) => p.id === product.id);
      if (productIndex !== -1) {
        state[productIndex] = product;
      } else {
        newProducts.push(product);
      }
    });

    return [...state, ...newProducts];
  }

  if (action.type === "UPDATE_PRODUCTS") {
    const product = action.payload;
    const productIndex = state.findIndex((p) => p.id === product.id);

    if (productIndex !== -1) {
      state[productIndex] = product;
      return [...state];
    } else {
      return [product, ...state];
    }
  }

  if (action.type === "DELETE_PRODUCT") {
    const productId = action.payload;
    const productIndex = state.findIndex((p) => p.id === productId);
    if (productIndex !== -1) {
      state.splice(productIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const ProductsTab = () => {
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [products, dispatch] = useReducer(reducer, []);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, categoryId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchProducts = async () => {
        try {
          const { data } = await api.get("/products", {
            params: { searchParam, pageNumber, categoryId },
          });
          dispatch({ type: "LOAD_PRODUCTS", payload: data.products });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchProducts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, categoryId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await api.get("/categories");
        setCategories(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("product", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PRODUCTS", payload: data.product });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PRODUCT", payload: +data.productId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenModal = () => {
    setSelectedProduct(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleCategoryChange = (event) => {
    setCategoryId(event.target.value);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prevState) => prevState + 1);
    }
  };

  return (
    <div className={classes.root} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ProductModal
        open={modalOpen}
        onClose={handleCloseModal}
        productId={selectedProduct?.id}
        onProduct={(product) => {
          if (selectedProduct) {
            dispatch({ type: "UPDATE_PRODUCTS", payload: product });
          } else {
            dispatch({ type: "LOAD_PRODUCTS", payload: [product] });
          }
          handleCloseModal();
        }}
      />
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <Box className={classes.searchAndAddContainer}>
            <TextField
              className={classes.searchInput}
              placeholder={i18n.t("catalog.searchPlaceholder", "Buscar productos...")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            {user?.profile === "admin" && (
              <Button
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
              startIcon={<AddIcon />}
              className={classes.addButton}
            >
              {i18n.t("catalog.buttons.add", "Añadir producto")}
            </Button>
            )}
            
          </Box>
          <Box className={classes.filtersContainer}>
            <FormControl variant="outlined" size="small" className={classes.filterItem}>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={categoryId}
                onChange={handleCategoryChange}
                label="Categoría"
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      
      <div 
        className={classes.mainPaper}
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto' }}
      >
        <Grid container spacing={2} className={classes.productsGrid}>
          {loading && !products.length ? (
            // Show skeletons while loading initial data
            [...Array(8)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
                <ProductCardSkeleton />
              </Grid>
            ))
          ) : (
            products.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <ProductCard
                  product={product}
                  onEdit={() => {

                    if(user?.profile === "admin") {
                    setSelectedProduct(product);
                    setModalOpen(true);
                    }
                  }}
                />
              </Grid>
            ))
          )}
          {loading && products.length > 0 && (
            // Show additional skeletons when loading more
            [...Array(4)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-more-${index}`}>
                <ProductCardSkeleton />
              </Grid>
            ))
          )}
        </Grid>
      </div>
    </div>
  );
};

export default ProductsTab;